/**
 * Foxhole world atlas — static layout of every hex region in a single
 * coordinate space. The world is rendered as one giant SVG with all hex
 * tiles placed at their canonical pixel offsets (origin at (0,0); tiles
 * drawn centered on `x,y`).
 */

/** Native pixel dimensions of every Foxhole hex region asset. */
export const TILE_W = 1024;
export const TILE_H = 888;

/** Horizontal step factor for the hexagonal tiling (`q = 3/4`). */
const Q = 3 / 4;
const W = TILE_W;
const H = TILE_H;

/**
 * Available map background flavours:
 *  - `default`: standard atlas (`/maps/`)
 *  - `imm`: satellite/immersive atlas (`/maps_imm/`)
 *  - `ink`: "Ink & Frontline" community mod by Arkhitecton (`/maps_ink/`)
 */
export type MapBackgroundKind = 'default' | 'imm' | 'ink';

export interface AtlasTile {
  key: string;
  mapName: string;
  x: number;
  y: number;
  asset: string;
  immAsset: string;
  inkAsset: string;
}

function tile(key: string, cx: number, cy: number, mapNameOverride?: string): AtlasTile {
  const mapName = mapNameOverride ?? key.replace(/^Map/, '');
  return {
    key,
    mapName,
    x: cx,
    y: cy,
    asset: `/maps/${key}.png`,
    immAsset: `/maps_imm/${key}.png`,
    inkAsset: `/maps_ink/${key}.png`,
  };
}

/** Returns the tile image URL for the given background flavour. */
export function tileAssetFor(tile: AtlasTile, background: MapBackgroundKind): string {
  switch (background) {
    case 'imm':
      return tile.immAsset;
    case 'ink':
      return tile.inkAsset;
    default:
      return tile.asset;
  }
}

export const ATLAS: readonly AtlasTile[] = [
  tile('MapOlavisWakeHex', -W * Q * 6, -H),

  tile('MapPariPeakHex', -W * Q * 5, -H * 1.5),
  tile('MapPalantineBermHex', -W * Q * 5, -H * 0.5),
  tile('MapOarbreakerHex', -W * Q * 5, +H * 0.5),

  tile('MapKuuraStrandHex', -W * Q * 4, -H * 2),
  tile('MapGutterHex', -W * Q * 4, -H),
  tile('MapFishermansRowHex', -W * Q * 4, 0),
  tile('MapStemaLandingHex', -W * Q * 4, +H),

  tile('MapNevishLineHex', -W * Q * 3, -H * 1.5),
  tile('MapFarranacCoastHex', -W * Q * 3, -H * 0.5),
  tile('MapWestgateHex', -W * Q * 3, +H * 0.5),
  tile('MapOriginHex', -W * Q * 3, +H * 1.5),

  tile('MapCallumsCapeHex', -W * Q * 2, -H * 2),
  tile('MapStonecradleHex', -W * Q * 2, -H),
  tile('MapKingsCageHex', -W * Q * 2, 0),
  tile('MapSableportHex', -W * Q * 2, +H),
  tile('MapAshFieldsHex', -W * Q * 2, +H * 2),

  tile('MapSpeakingWoodsHex', -W * Q, -H * 2.5),
  tile('MapMooringCountyHex', -W * Q, -H * 1.5),
  tile('MapLinnMercyHex', -W * Q, -H * 0.5),
  tile('MapLochMorHex', -W * Q, +H * 0.5),
  tile('MapHeartlandsHex', -W * Q, +H * 1.5),
  tile('MapRedRiverHex', -W * Q, +H * 2.5),

  tile('MapBasinSionnachHex', 0, -H * 3),
  tile('MapReachingTrailHex', 0, -H * 2),
  tile('MapCallahansPassageHex', 0, -H),
  tile('MapDeadLandsHex', 0, 0),
  tile('MapUmbralWildwoodHex', 0, +H),
  tile('MapGreatMarchHex', 0, +H * 2),
  tile('MapKalokaiHex', 0, +H * 3),

  tile('MapHowlCountyHex', +W * Q, -H * 2.5),
  tile('MapViperPitHex', +W * Q, -H * 1.5),
  tile('MapMarbanHollowHex', +W * Q, -H * 0.5, 'MarbanHollow'),
  tile('MapDrownedValeHex', +W * Q, +H * 0.5),
  tile('MapShackledChasmHex', +W * Q, +H * 1.5),
  tile('MapAcrithiaHex', +W * Q, +H * 2.5),

  tile('MapClansheadValleyHex', +W * Q * 2, -H * 2),
  tile('MapWeatheredExpanseHex', +W * Q * 2, -H),
  tile('MapClahstraHex', +W * Q * 2, 0),
  tile('MapAllodsBightHex', +W * Q * 2, +H),
  tile('MapTerminusHex', +W * Q * 2, +H * 2),

  tile('MapMorgensCrossingHex', +W * Q * 3, -H * 1.5),
  tile('MapStlicanShelfHex', +W * Q * 3, -H * 0.5),
  tile('MapEndlessShoreHex', +W * Q * 3, +H * 0.5),
  tile('MapReaversPassHex', +W * Q * 3, +H * 1.5),

  tile('MapGodcroftsHex', +W * Q * 4, -H),
  tile('MapTempestIslandHex', +W * Q * 4, 0),
  tile('MapWrestaHex', +W * Q * 4, +H),
  tile('MapOnyxHex', +W * Q * 4, +H * 2),

  tile('MapLykosIsleHex', +W * Q * 5, -H * 0.5),
  tile('MapTheFingersHex', +W * Q * 5, +H * 0.5),
  tile('MapTyrantFoothillsHex', +W * Q * 5, +H * 1.5),

  tile('MapPipersEnclaveHex', +W * Q * 6, +H),
];

export const REGION_LABELS: Readonly<Record<string, string>> = {
  MapOlavisWakeHex: "Olavi's Wake",
  MapPariPeakHex: 'Pari Peak',
  MapPalantineBermHex: 'Palantine Berm',
  MapOarbreakerHex: 'The Oarbreaker Isles',
  MapKuuraStrandHex: 'Kuura Strand',
  MapGutterHex: 'The Gutter',
  MapFishermansRowHex: "Fisherman's Row",
  MapStemaLandingHex: 'Stema Landing',
  MapNevishLineHex: 'Nevish Line',
  MapFarranacCoastHex: 'Farranac Coast',
  MapWestgateHex: 'Westgate',
  MapOriginHex: 'Origin',
  MapCallumsCapeHex: "Callum's Cape",
  MapStonecradleHex: 'Stonecradle',
  MapKingsCageHex: "King's Cage",
  MapSableportHex: 'Sableport',
  MapAshFieldsHex: 'Ash Fields',
  MapSpeakingWoodsHex: 'Speaking Woods',
  MapMooringCountyHex: 'The Moors',
  MapLinnMercyHex: 'The Linn of Mercy',
  MapLochMorHex: 'Loch M\u00f3r',
  MapHeartlandsHex: 'The Heartlands',
  MapRedRiverHex: 'Red River',
  MapBasinSionnachHex: 'Basin Sionnach',
  MapReachingTrailHex: 'Reaching Trail',
  MapCallahansPassageHex: "Callahan's Passage",
  MapDeadLandsHex: 'Deadlands',
  MapUmbralWildwoodHex: 'Umbral Wildwood',
  MapGreatMarchHex: 'Great March',
  MapKalokaiHex: 'Kalokai',
  MapHowlCountyHex: 'Howl County',
  MapViperPitHex: 'Viper Pit',
  MapMarbanHollowHex: 'Marban Hollow',
  MapDrownedValeHex: 'The Drowned Vale',
  MapShackledChasmHex: 'Shackled Chasm',
  MapAcrithiaHex: 'Acrithia',
  MapClansheadValleyHex: 'Clanshead Valley',
  MapWeatheredExpanseHex: 'Weathered Expanse',
  MapClahstraHex: 'The Clahstra',
  MapAllodsBightHex: "Allod's Bight",
  MapTerminusHex: 'Terminus',
  MapMorgensCrossingHex: "Morgen's Crossing",
  MapStlicanShelfHex: 'Stlican Shelf',
  MapEndlessShoreHex: 'Endless Shore',
  MapReaversPassHex: "Reaver's Pass",
  MapGodcroftsHex: 'Godcrofts',
  MapTempestIslandHex: 'Tempest Island',
  MapWrestaHex: 'Wresta',
  MapOnyxHex: '\u00d3nyx',
  MapLykosIsleHex: 'Lykos Isle',
  MapTheFingersHex: 'The Fingers',
  MapTyrantFoothillsHex: 'Tyrant Foothills',
  MapPipersEnclaveHex: "Piper's Enclave",
};

export const WORLD_BOUNDS = {
  minX: -W * Q * 6 - W / 2,
  maxX: +W * Q * 6 + W / 2,
  minY: -H * 3 - H / 2,
  maxY: +H * 3 + H / 2,
} as const;

export const HEX_VERTICES: ReadonlyArray<{ x: number; y: number }> = [
  { x: -W / 2, y: 0 },
  { x: -W / 4, y: -H / 2 },
  { x: +W / 4, y: -H / 2 },
  { x: +W / 2, y: 0 },
  { x: +W / 4, y: +H / 2 },
  { x: -W / 4, y: +H / 2 },
];

export const RDZ_INSET = 150 / 2.1328125;
export const RMETER = 2.1328125;
export const INNER_GRID_STEP = 125 / 2.1328125;
export const INNER_GRID_ROWS = 16;
export const INNER_GRID_COLS = 18;

// ---------------------------------------------------------------------------
// Region lookup helpers
// ---------------------------------------------------------------------------

function distSq(ax: number, ay: number, bx: number, by: number): number {
  return (ax - bx) ** 2 + (ay - by) ** 2;
}

/** Returns the ATLAS tile whose center is closest to the given world point. */
export function findNearestTile(wx: number, wy: number): AtlasTile | null {
  let best: AtlasTile | null = null;
  let bestDist = Infinity;
  for (const tile of ATLAS) {
    const d = distSq(wx, wy, tile.x, tile.y);
    if (d < bestDist) {
      bestDist = d;
      best = tile;
    }
  }
  return best;
}

/** Returns the canonical region key (ATLAS tile.key) for a world point. */
export function findRegionKey(wx: number, wy: number): string | null {
  return findNearestTile(wx, wy)?.key ?? null;
}

/** Returns a human-readable region label for a world point. */
export function findRegionLabel(wx: number, wy: number): string {
  const tile = findNearestTile(wx, wy);
  if (!tile) return '—';
  return REGION_LABELS[tile.key] ?? tile.mapName;
}
